import Vue from 'vue'
import Core from 'core/js/components/stop-show/content.js'
import RealTimeStopEstimationsService from 'core/js/services/api/tracking/real-time-stop-estimations'

export default Vue.extend({
  extends: Core,
  name: 'stop-show-content-vl',

  data() {
    return {
      // Flattened ATMV realtime estimations.
      // Item shape:
      // { routeLabel, lineCode, line, estimation: number (seconds), distance?, vehicleId?, time: number (minutes) }
      realTimeFlat: []
    }
  },

  methods: {

     /**
     * VL override.
     *
     * - Calls ATMV proxy and adapts payload to core's lastPositionBuses contract.
     * - Normalizes response shapes: supports array or { data: [...] }.
     * - Filters invalid/too-distant estimations (using maxBusTime).
     * - Maintains loading/retry scheduling consistent with core.
     */
    async loadNextBuses() {
      if (!this.loadingBuses && this.timeoutRun) {
        this.loadingBuses = true
        this.timeZone = this.$municipality.time_zone
        const currentDateFormated = new Intl.DateTimeFormat('es-ES', {
          timeZone: this.timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit',
          year: 'numeric', month: 'long', day: 'numeric', hour12: false}).format(new Date());

        // Convert formatted string to seconds-since-midnight (core helper).
        this.thisTime = this.hourInSecond(currentDateFormated)

        if (!this.stop?.code) {
          this.loadingBuses = false
          return
        }
        const params = { stop_id: this.stop.code }

        try {
          const response = await RealTimeStopEstimationsService.index({params, skipLoading: true})

          const payload = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : []

          if (!payload.length) {
            this.realTimeFlat = []
            this.lastPositionBuses = []
            this.busesRequestFinished = true
            this.loadingBuses = false
            return
          }

          const flat = []

          // Traverse hierarchical ATMV payload and flatten entries.
          payload.forEach(lineGroup => {
            const line = lineGroup?.line
            const lineCode = lineGroup?.lineCode

            ;(lineGroup?.estimations || []).forEach(routeGroup => {
              const routeCode = routeGroup?.routeCode

              ;(routeGroup?.estimations || []).forEach(bus => {
                const estimationSeconds = Number(bus?.estimation)

                // Keep only positive estimations within configured window.
                if (
                  estimationSeconds > 0 &&
                  estimationSeconds <= (this.maxBusTime * 60)
                ) {
                  flat.push({
                    routeLabel: routeGroup?.routeLabel,
                    lineCode,
                    line,
                    estimation: estimationSeconds,
                    distance: bus?.distance,
                    vehicleId: bus?.vehicleId,
                    time: Math.round(estimationSeconds / 60)
                  })
                }
              })
            })
          })

          const sortedFlat = flat.sort((a, b) => a.estimation - b.estimation)

          this.realTimeFlat = sortedFlat
          this.lastPositionBuses = sortedFlat

          if (this.entryPointGetters.previousLocation === 'map') {
            this.sortAndFilterPlannedSchedules()
          }

          this.busesRequestFinished = true
        } catch (error) {

          this.realTimeFlat = []
          this.lastPositionBuses = []
          this.busesRequestFinished = true
          this.loadingBuses = false

          this.requestTimeout = setTimeout(this.loadNextBuses, this.retryTime)
          return

        }

        if (this.firstLoadingData) {
          this.showInfo = true

          const realTimePresence = this.plannedSchedules.some(
            planned => this.filtrateLastPositionBuses(planned).length > 0
          )

          this.tab = realTimePresence ? 0 : 1
          this.firstLoadingData = false
        }

        this.loadingBuses = false

        this.requestTimeout = setTimeout(
          this.loadNextBuses,
          this.reloadBusesTime
        )
      }
    },

    /**
     * Compatibility bridge for core.
     * Delegates to strict matching implementation.
     */
    filtrateLastPositionBuses(schedule) {
      return this.matchRealTimeForPlanned(schedule)
    },

    /**
     * Matches realtime estimations against a planned schedule.
     *
     * Matching strategy:
     * - route_code is mandatory (strict equality with rt.routeLabel).
     * - line name (subline) must also match when available.
     * - Supports both flows:
     *     • Stop detail (route_code in root)
     *     • Journey start (route_code inside sublineDetails.lineInfo)
     *
     * Returns:
     *   Array of realtime matches from realTimeFlat.
     */
    matchRealTimeForPlanned(plannedSchedule) {
      if (!plannedSchedule) return []

      const plannedRouteCode = plannedSchedule.route_code || plannedSchedule.sublineDetails?.lineInfo?.route_code
      const plannedLineName = plannedSchedule.subline_name || plannedSchedule.sublineDetails?.lineName

      if (!plannedRouteCode) return []

      return (this.realTimeFlat || [])
        .filter(rt => {
          if (!rt.routeLabel) return false
          if (String(rt.routeLabel) !== String(plannedRouteCode)) return false

          if (plannedLineName && rt.line) {
            return String(rt.line) === String(plannedLineName)
          }
          return false
        })
    },

    /**
     * Prefer realtime display when available, otherwise planned time.
     *
     * - Expects seconds in `estimation`; uses global filter secondsToMinutes.
     * - No extra fallback here: filter output is returned directly.
     */
    showOneTimeRealOrPlanned(journey = undefined) {
      let lastPositionFiltered = []
      let time

      if (journey) {
        lastPositionFiltered = this.filtrateLastPositionBuses(journey)
        time = journey?.schedules?.[0]?.time
      }

      if (lastPositionFiltered && lastPositionFiltered.length > 0 && !this.hideRealTime) {
        const seconds = Number(lastPositionFiltered[0]?.estimation || 0)

        const secondsToMinutes = Vue.options.filters.secondsToMinutes

        return secondsToMinutes(seconds)
      } else if (this.plannedSchedules && this.plannedSchedules.length > 0) {
        return time
      }

      return time
    },

    /**
     * Resolve icon for first bus: realtime -> rss-green, else planned -> calendar-check.
     */
    firstBusIconUrl(journey = undefined) {
      let lastPositionFiltered = []

      if (journey) {
        lastPositionFiltered = this.filtrateLastPositionBuses(journey)
      }

      if (this.lastPositionBuses && lastPositionFiltered.length > 0 && !this.hideRealTime) {
        return require('assets/images/rss-green.svg')
      } else if (this.plannedSchedules && this.plannedSchedules.length > 0) {
        return require('assets/images/calendar-check.svg')
      }
      return ''
    },

    /**
     * Adds route identifiers to plannedSchedules in stop flow
     * to enable realtime matching. Journey flow already exposes them.
     */
    formatDataPlannedSchedules(response) {
      Core.options.methods.formatDataPlannedSchedules.call(this, response)

      const items = Array.isArray(response)
        ? response
        : response?.data

      if (!Array.isArray(items)) return

      items.forEach(item => {
        if (!item?.code) return

        const target = this.plannedSchedules.find(
          ps => ps.id === item.code
        )

        if (!target) return

        target.route_code = item.route_code || null
        target.subline_name = item.subline_name || null
      })
    }

  }
})